import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Centered, Eyebrow } from "./approve-impersonation";

/**
 * Customer-facing MFA-reset page (WARD-47). Reached via the emailed link. The
 * reset only happens on an explicit click — there is no staff-override path,
 * so this page is the sole way the secret is cleared.
 */
type State = "idle" | "working" | "done" | "error";

export function ResetMfaRoute() {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [state, setState] = useState<State>("idle");
  const [error, setError] = useState("");

  async function reset() {
    setState("working");
    setError("");
    try {
      const res = await fetch("/bff/mfa/reset", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ token }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(reason(body.error));
        setState("error");
        return;
      }
      setState("done");
    } catch {
      setError("Something went wrong. Please try again.");
      setState("error");
    }
  }

  return (
    <Centered>
      <Eyebrow>two-factor reset</Eyebrow>
      <h1 className="mt-4 font-sans text-2xl font-medium tracking-tight text-foreground">
        Reset two-factor authentication
      </h1>
      {state === "done" ? (
        <p className="mt-4 text-[14px] leading-relaxed text-muted-foreground">
          Your two-factor authentication has been reset. The next time you sign in you can set up a
          new authenticator.
        </p>
      ) : !token ? (
        <p className="mt-4 text-[14px] text-muted-foreground">
          This link is missing its token. Please use the button in the email we sent you.
        </p>
      ) : (
        <>
          <p className="mt-4 max-w-[34rem] text-[14px] leading-relaxed text-muted-foreground">
            This clears your current authenticator so you can set up a new one. Only do this if you
            requested it — no one can reset your two-factor without this link.
          </p>
          {error && (
            <p className="mt-4 rounded-md border border-[#FFB85C]/30 bg-[#FFB85C]/10 px-3 py-2 text-[13px] text-[#FFB85C]">
              {error}
            </p>
          )}
          <Button type="button" onClick={reset} disabled={state === "working"} className="mt-6">
            {state === "working" ? "Resetting…" : "Reset two-factor"}
          </Button>
        </>
      )}
    </Centered>
  );
}

function reason(code: string | undefined): string {
  switch (code) {
    case "expired":
      return "This reset link has expired. Request a new one.";
    case "already_consumed":
      return "This link has already been used.";
    default:
      return "This reset link is invalid.";
  }
}
